import { useMemo } from 'react';
import { cn } from '../utils/cn';

/** Strip § codes and JSON-style quotes for search / plain matching */
export function stripLegacyMinecraftFormatting(input: string): string {
  return String(input)
    .replace(/§[0-9a-fk-or]/gi, '')
    .replace(/^["']+|["']+$/g, '')
    .replace(/"/g, '');
}

/*
 * Legacy § color codes map to theme-aware CSS variables (see index.css). In dark
 * theme these are the authentic Minecraft colors; in light theme they are
 * remapped to keep the same hue while staying legible on light surfaces.
 */
const COLOR_CLASS: Record<string, string> = {
  '0': 'text-[var(--mc-0)]',
  '1': 'text-[var(--mc-1)]',
  '2': 'text-[var(--mc-2)]',
  '3': 'text-[var(--mc-3)]',
  '4': 'text-[var(--mc-4)]',
  '5': 'text-[var(--mc-5)]',
  '6': 'text-[var(--mc-6)]',
  '7': 'text-[var(--mc-7)]',
  '8': 'text-[var(--mc-8)]',
  '9': 'text-[var(--mc-9)]',
  a: 'text-[var(--mc-a)]',
  b: 'text-[var(--mc-b)]',
  c: 'text-[var(--mc-c)]',
  d: 'text-[var(--mc-d)]',
  e: 'text-[var(--mc-e)]',
  f: 'text-[var(--mc-f)]',
};

function normalizeLegacyInput(raw: string): string {
  return String(raw).replace(/^["']+|["']+$/g, '').replace(/"/g, '');
}

function parseLegacyToSegments(input: string): { text: string; color: string; bold: boolean; italic: boolean; underline: boolean; strikethrough: boolean }[] {
  const str = normalizeLegacyInput(input);
  const segments: { text: string; color: string; bold: boolean; italic: boolean; underline: boolean; strikethrough: boolean }[] = [];

  let color = 'text-[var(--mc-f)]';
  let bold = false;
  let italic = false;
  let underline = false;
  let strikethrough = false;

  let i = 0;
  let buf = '';

  const flush = () => {
    if (!buf) return;
    segments.push({ text: buf, color, bold, italic, underline, strikethrough });
    buf = '';
  };

  while (i < str.length) {
    const j = str.indexOf('§', i);
    if (j === -1) {
      buf += str.slice(i);
      flush();
      break;
    }
    buf += str.slice(i, j);
    flush();
    if (j + 1 >= str.length) break;
    const code = str[j + 1].toLowerCase();
    i = j + 2;

    if (code === 'r') {
      color = 'text-[var(--mc-f)]';
      bold = false;
      italic = false;
      underline = false;
      strikethrough = false;
      continue;
    }
    if (code === 'l') {
      bold = true;
      continue;
    }
    if (code === 'm') {
      strikethrough = true;
      continue;
    }
    if (code === 'n') {
      underline = true;
      continue;
    }
    if (code === 'o') {
      italic = true;
      continue;
    }
    if (COLOR_CLASS[code]) {
      color = COLOR_CLASS[code];
      continue;
    }
    // Unknown code (e.g. §x hex in newer versions): skip
  }

  return segments;
}

type LegacyMinecraftTextProps = {
  text: string | null | undefined;
  className?: string;
};

export function LegacyMinecraftText({ text, className }: LegacyMinecraftTextProps) {
  const segments = useMemo(() => {
    if (text == null || text === '') return [];
    return parseLegacyToSegments(String(text));
  }, [text]);

  if (segments.length === 0) {
    return <span className={className} />;
  }

  return (
    <span className={className}>
      {segments.map((seg, idx) => (
        <span
          key={idx}
          className={cn(
            seg.color,
            seg.bold && 'font-bold',
            seg.italic && 'italic',
            seg.underline && 'underline',
            seg.strikethrough && 'line-through'
          )}
        >
          {seg.text}
        </span>
      ))}
    </span>
  );
}
